import {
    Button,
    Divider,
    Grid,
    Paper,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { REQUIRED_RED } from '../../../App';
import { FormatNumberForKmFloat } from '../../../components/FormatNumber/FormatNumber';
import { MuiDisablesTextFieldWithFocus40, MuiTextFieldWithFocus, MuiTextFieldWithFocus40 } from '../../../config/MaterialUIConstants';
import { getDefaultShopPrice, updateDefaultShopPrice } from '../../../redux/Account/ShopDetails/action';

function ShopPricing({ setShowLoader, setOpenSuccessModal, setSuccessMessage }) {
    const dispatch = useDispatch();
    const [defaultPriceDetails, setDefaultPriceDetails] = useState(null);
    // Force re-render when pricing method changes
    const [, forceUpdate] = useState({});

    const {
        handleSubmit,
        setValue,
        control,
        clearErrors,
        getValues,
        setError,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            defaultHourlyRate: '',
            defaultHourlyCost: '',
            defaultMarkup: '',
            defaultMargin: '',
            defaultPricingMethod: 'markup',
        },
    });

    const shopPriceDetails = useSelector((state) => state?.account?.defaultShopPrice);

    // Watch for pricing method changes to update checkboxes immediately
    const pricingMethod = watch('defaultPricingMethod');

    // Fetch shop price details on component mount
    useEffect(() => {
        dispatch(getDefaultShopPrice());
    }, [dispatch]);

    // Update form values when shop price details are fetched
    useEffect(() => {
        if (shopPriceDetails) {
            setValue('defaultHourlyRate', shopPriceDetails?.priceBase?.labor?.defaultHourlyRate || 0);
            setValue('defaultHourlyCost', shopPriceDetails?.priceBase?.labor?.defaultHourlyCost || 0);
            setValue('defaultMarkup', shopPriceDetails?.priceBase?.inventory?.defaultMarkup || 0);
            setValue('defaultMargin', shopPriceDetails?.priceBase?.inventory?.defaultMargin || 0);
            setValue('defaultPricingMethod', shopPriceDetails?.priceBase?.inventory?.defaultPricingMethod || 'markup');
            setDefaultPriceDetails(shopPriceDetails);
        }
    }, [shopPriceDetails, setValue]);

    // Handle form submission
    const onSubmitShopServicePricing = async (values) => {
        // Create payload matching the JSON structure you provided
        const payload = {
            labor: {
                defaultHourlyRate: Number(values.defaultHourlyRate),
                defaultHourlyCost: Number(values.defaultHourlyCost),
            },
            inventory: {
                defaultMarkup: Number(values.defaultMarkup),
                defaultMargin: Number(values.defaultMargin),
                defaultPricingMethod: values.defaultPricingMethod,
            },
        };

        setShowLoader(true);

        try {
            const result = await dispatch(updateDefaultShopPrice(payload));
            if (result) {
                setOpenSuccessModal(true);
                setSuccessMessage('Pricing changes saved');
                setDefaultPriceDetails(result);
            }
        } catch (error) {
            console.error('Error updating shop price details:', error);
        } finally {
            setShowLoader(false);
        }
    };

    // Helper function to handle numeric input with decimal validation
    const handleNumericInput = (event, fieldName, maxValue = null) => {
        const inputValue = event.target.value;
        const numericValue = inputValue.replace(/[^\d.]/g, '');

        if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
            if (maxValue !== null && Number(numericValue) > maxValue) {
                setError(fieldName, {
                    message: `Value must be between 0 and ${maxValue}`,
                });
            } else {
                clearErrors(fieldName);
            }
            setValue(fieldName, numericValue);
        }
    };

    // Handler for canceling changes
    const handleCancelChanges = () => {
        if (shopPriceDetails) {
            setValue('defaultHourlyRate', shopPriceDetails?.priceBase?.labor?.defaultHourlyRate || 0);
            setValue('defaultHourlyCost', shopPriceDetails?.priceBase?.labor?.defaultHourlyCost || 0);
            setValue('defaultMarkup', shopPriceDetails?.priceBase?.inventory?.defaultMarkup || 0);
            setValue('defaultMargin', shopPriceDetails?.priceBase?.inventory?.defaultMargin || 0);
            setValue('defaultPricingMethod', shopPriceDetails?.priceBase?.inventory?.defaultPricingMethod || 'markup');
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmitShopServicePricing)} style={{ marginTop: '20px' }}>
                {/* Main container */}
                <Grid
                    container
                    sx={{ boxShadow: 3, bgcolor: 'white' }}
                    justifyContent="space-between"
                    padding="24px"
                    borderTop="none"
                    borderRadius="10px"
                    marginTop="32px"
                >
                    {/* Header section */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                            Shop Pricing
                        </Typography>
                    </Grid>

                    {/* Content section */}
                    <Grid item xs={12} md={8} spacing={3}>
                        <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            xl={12}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={3}
                            padding="16px 0"
                        >
                            {/* Labor Rate */}
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" className="label-500-8">
                                    Labor Rate
                                    <span style={{ color: REQUIRED_RED, marginLeft: '4px' }}>*</span>
                                </Typography>
                                <Controller
                                    name="defaultHourlyRate"
                                    control={control}
                                    rules={{
                                        required: 'Please enter service rate',
                                        validate: (value) => value < 1000 || 'Rate per hour should be less than 1000',
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            variant="outlined"
                                            fullWidth
                                            error={!!errors.defaultHourlyRate}
                                            helperText={errors.defaultHourlyRate ? errors.defaultHourlyRate.message : ''}
                                            value={FormatNumberForKmFloat(field.value)}
                                            onChange={(event) => handleNumericInput(event, 'defaultHourlyRate', 999.99)}
                                            InputLabelProps={{ shrink: field.value?.length > 0 }}
                                            placeholder="$0.00"
                                            sx={MuiTextFieldWithFocus}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Labor Cost */}
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" className="label-500-8">
                                    Labor Cost
                                    <span style={{ color: REQUIRED_RED, marginLeft: '4px' }}>*</span>
                                </Typography>
                                <Controller
                                    name="defaultHourlyCost"
                                    control={control}
                                    rules={{
                                        required: 'Please enter labor cost',
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            variant="outlined"
                                            fullWidth
                                            error={!!errors.defaultHourlyCost}
                                            helperText={errors.defaultHourlyCost ? errors.defaultHourlyCost.message : ''}
                                            value={FormatNumberForKmFloat(field.value)}
                                            onChange={(event) => handleNumericInput(event, 'defaultHourlyCost')}
                                            InputLabelProps={{ shrink: field.value?.length > 0 }}
                                            placeholder="$0.00"
                                            sx={MuiTextFieldWithFocus}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            <Grid item xs={12}>
                                <TableContainer
                                    component={Paper}
                                    style={{
                                        boxShadow: 'none',
                                        overflowX: 'inherit',
                                        border: '1px solid var(--box-stroke-10, rgba(0, 0, 0, 0.10))',
                                        background: 'var(--PureWhite, #FFF)',
                                        boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                        padding: '12px',
                                    }}
                                    sx={{
                                        borderRadius: '8px!important',
                                    }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow style={{ backgroundColor: '#F8F8F8' }}>
                                                <TableCell style={{ border: 'none', fontWeight: '600', width: '75%', padding: '8px 16px' }}>
                                                    Pricing Method
                                                </TableCell>
                                                <TableCell style={{ border: 'none', fontWeight: '600', width: '15%', padding: '8px 16px' }}>Value</TableCell>
                                                <TableCell style={{ border: 'none', fontWeight: '600', padding: '8px 16px', textAlign: 'end' }}>
                                                    Default
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Default Markup % */}
                                            <TableRow>
                                                <TableCell style={{ border: 'none', fontSize: '16px', fontWeight: '500', padding: '12px 16px' }}>
                                                    Markup %
                                                </TableCell>

                                                <TableCell style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Controller
                                                        name="defaultMarkup"
                                                        control={control}
                                                        rules={{
                                                            validate: (value) => {
                                                                if (value === '.') {
                                                                    return 'Cost cannot be just a decimal point.';
                                                                }
                                                                return true;
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                variant="outlined"
                                                                fullWidth
                                                                error={!!errors.defaultMarkup}
                                                                helperText={errors.defaultMarkup ? errors.defaultMarkup.message : ''}
                                                                value={field.value}
                                                                onChange={(event) => handleNumericInput(event, 'defaultMarkup')}
                                                                InputLabelProps={{ shrink: field.value?.length > 0 }}
                                                                sx={MuiTextFieldWithFocus40}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                <TableCell align="right" style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Radio
                                                        checked={getValues().defaultPricingMethod === 'markup'}
                                                        onChange={() => {
                                                            setValue('defaultPricingMethod', 'markup');
                                                            // Force re-render to immediately show the change
                                                            setValue('defaultMargin', getValues().defaultMargin);
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>

                                            {/* Default Margin % */}
                                            <TableRow>
                                                <TableCell style={{ border: 'none', fontSize: '16px', fontWeight: '500', padding: '12px 16px' }}>
                                                    Margin %
                                                </TableCell>

                                                <TableCell style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Controller
                                                        name="defaultMargin"
                                                        control={control}
                                                        rules={{
                                                            validate: (value) => {
                                                                if (value === '.') {
                                                                    return 'Cost cannot be just a decimal point.';
                                                                }
                                                                if (value > 99.99) {
                                                                    return 'Value must be between 0 and 100';
                                                                }
                                                                return true;
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                variant="outlined"
                                                                fullWidth
                                                                error={!!errors.defaultMargin}
                                                                helperText={errors.defaultMargin ? errors.defaultMargin.message : ''}
                                                                value={field.value}
                                                                onChange={(event) => handleNumericInput(event, 'defaultMargin', 100)}
                                                                InputLabelProps={{ shrink: field.value?.length > 0 }}
                                                                sx={MuiTextFieldWithFocus40}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                <TableCell align="right" style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Radio
                                                        checked={getValues().defaultPricingMethod === 'margin'}
                                                        onChange={() => {
                                                            setValue('defaultPricingMethod', 'margin');
                                                            // Force re-render to immediately show the change
                                                            setValue('defaultMarkup', getValues().defaultMarkup);
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell style={{ border: 'none', fontSize: '16px', fontWeight: '500', padding: '12px 16px' }}>
                                                    Manual Price
                                                </TableCell>

                                                <TableCell style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Controller
                                                        name="retailPrice"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                variant="outlined"
                                                                placeholder="-"
                                                                fullWidth
                                                                error={!!errors.retailPrice}
                                                                helperText={errors.retailPrice ? errors.retailPrice.message : ''}
                                                                value={field.value}
                                                                onChange={(event) => handleNumericInput(event, 'retailPrice', 100)}
                                                                InputLabelProps={{ shrink: field.value?.length > 0 }}
                                                                sx={MuiDisablesTextFieldWithFocus40}
                                                                disabled
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                <TableCell align="right" style={{ border: 'none', padding: '12px 16px' }}>
                                                    <Radio
                                                        checked={getValues().defaultPricingMethod === 'retail_price'}
                                                        onChange={() => {
                                                            setValue('defaultPricingMethod', 'retail_price');
                                                            // Force re-render to immediately show the change
                                                            setValue('defaultMarkup', getValues().defaultMarkup);
                                                            setValue('defaultMargin', getValues().defaultMargin);
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* Action buttons */}
                            <Grid item justifyContent="flex-end" width={'100%'}>
                                <Stack spacing={2} direction="row" justifyContent="flex-end">
                                    <Button className="btn-text-size-manage" variant="outlined" color="primary" onClick={handleCancelChanges}>
                                        Cancel
                                    </Button>
                                    <Button className="btn-text-size-manage" variant="contained" color="primary" type="submit">
                                        Save
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </>
    );
}

export default ShopPricing;
